import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CopyableText from './CopyableText';

export interface DetailRow {
  label: React.ReactNode;
  value?: React.ReactNode;
  /** Render the value via CopyableText (truncate + copy). Good for IDs/hashes. */
  copyable?: boolean;
  /** Monospaced "code" style for the value (implies technical value). */
  mono?: boolean;
  /** Raw string to copy when `copyable` and `value` is a custom node. */
  copyValue?: string;
  /** Hide this row entirely. */
  hidden?: boolean;
  /** Make the row span the full width (value below the label). */
  fullWidth?: boolean;
}

export interface DetailSectionProps {
  title: React.ReactNode;
  /** Optional leading icon. */
  icon?: React.ReactNode;
  /** Label/value rows rendered as a clean definition grid. */
  rows?: DetailRow[];
  /** Free-form content rendered below the rows. */
  children?: React.ReactNode;
  /** Start expanded. Default false (collapsed — secondary info). */
  defaultExpanded?: boolean;
  /** Render as a plain titled block instead of a collapsible accordion. */
  static?: boolean;
}

/**
 * Collapsible "details" container for secondary / technical information —
 * full IDs, hashes, metadata, notes, secondary dates, descriptions.
 *
 * Use inside list cards (as AccordionRowDetails), modals, and detail pages to
 * keep the primary view scannable while keeping everything one tap away.
 * Accessible (keyboard + ARIA via MUI Accordion) and theme-aware.
 */
const DetailSection: React.FC<DetailSectionProps> = ({
  title,
  icon,
  rows = [],
  children,
  defaultExpanded = false,
  static: isStatic = false,
}) => {
  const visibleRows = rows.filter((r) => !r.hidden);

  const body = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {visibleRows.map((row, idx) => (
        <Box
          key={idx}
          sx={{
            display: 'flex',
            flexDirection: row.fullWidth ? 'column' : 'row',
            alignItems: row.fullWidth ? 'stretch' : 'center',
            justifyContent: 'space-between',
            gap: row.fullWidth ? 0.5 : 2,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ flexShrink: 0, fontWeight: 500 }}
          >
            {row.label}
          </Typography>
          <Box sx={{ minWidth: 0, textAlign: row.fullWidth ? 'start' : 'end', maxWidth: '100%' }}>
            {row.copyable ? (
              <CopyableText
                value={row.copyValue ?? (typeof row.value === 'string' ? row.value : undefined)}
                display={row.value}
                mono={row.mono}
                truncate
              />
            ) : (
              <Typography
                variant="body2"
                component="span"
                sx={
                  row.mono
                    ? {
                        fontFamily: 'monospace',
                        fontSize: '0.8125rem',
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 1,
                        bgcolor: 'action.hover',
                      }
                    : { wordBreak: 'break-word' }
                }
              >
                {row.value ?? '-'}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
      {children}
    </Box>
  );

  const heading = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {icon && <Box sx={{ display: 'flex', color: 'text.secondary' }}>{icon}</Box>}
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
    </Box>
  );

  if (isStatic) {
    return (
      <Box sx={{ py: 1 }}>
        <Box sx={{ mb: 1.25 }}>{heading}</Box>
        {body}
      </Box>
    );
  }

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      elevation={0}
      square={false}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        '&:not(:last-child)': { mb: 1 },
        bgcolor: 'transparent',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ '& .MuiAccordionSummary-content': { my: 1 } }}
      >
        {heading}
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>{body}</AccordionDetails>
    </Accordion>
  );
};

export default DetailSection;
